/**
 * Every API path, grouped by domain. Mirrors HexagieApp
 * `src/config/apiConfig.ts` so a route is findable in the same place in both
 * codebases.
 *
 * Rules carried over from the app, worth keeping:
 *   - No URL is written anywhere outside this file.
 *   - Anything with a path parameter is a FUNCTION, so callers cannot
 *     hand-build a URL and cannot forget the trailing slash.
 *   - The trailing slash is mandatory. `/consumers/self` is a hard 404;
 *     `/consumers/self/` works.
 *   - Sapphire routes sit at the host root with no `/api` prefix. Geography
 *     and the identity routes are the only exceptions.
 */

import { API_BASE_URL } from "./env";

/**
 * Builds an optional query string, dropping empty values.
 * Mirrors the app's exported `qs()`.
 *
 * @param {Object.<string, (string|number|boolean|null|undefined)>} params
 * @returns {string} e.g. "?page=0&size=20", or "" when nothing is set
 */
export const qs = (params) => {
  const parts = Object.entries(params || {})
    .filter(([, value]) => value !== undefined && value !== null && value !== "")
    .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`);

  return parts.length ? `?${parts.join("&")}` : "";
};

const ENDPOINTS = {
  BASE_URL: API_BASE_URL,

  AUTH: {
    sessionStart: "/consumer/auth/session/start/",
    sessionVerify: "/consumer/auth/session/verify/",
    sessionDetails: "/consumer/auth/session/details/",
    otpRequest: "/otp/request/",
    otpVerify: "/otp/verify/",
    refresh: "/login/refresh/",
    userProfile: "/api/users/me/",
    updateUserProfile: "/api/users/profile/",
    logout: "/identities/logout/",
  },

  FILES: {
    uploadRequest: (fileName) =>
      `/files/upload-request/${encodeURIComponent(fileName)}/`,
  },

  ACCOUNT: {
    deactivationInitiate: "/consumer/account/deactivation/initiate/",
    deactivationConfirm: "/consumer/account/deactivation/confirm/",
    reactivationInitiate: "/consumer/account/reactivation/initiate/",
    reactivationConfirm: "/consumer/account/reactivation/confirm/",
  },

  CONSUMER: {
    self: "/consumers/self/",
    save: "/consumers/save/",
    preferences: "/consumers/self/preferences/",
    stats: "/consumers/self/stats/",
    consumption: "/consumers/self/consumption/",
  },

  GEOGRAPHY: {
    continents: "/api/geography/continents/",
    countries: "/api/geography/countries/",
    countryById: (id) => `/api/geography/countries/${id}/`,
    countryByIso2: (iso2) => `/api/geography/countries/iso/${iso2}/`,
    countriesByContinent: (continentId) =>
      `/api/geography/countries/continent/${continentId}/`,
    states: "/api/geography/states/",
    stateById: (id) => `/api/geography/states/${id}/`,
    cities: "/api/geography/cities/",
    cityById: (id) => `/api/geography/cities/${id}/`,
  },

  ADDRESS: {
    fromLocation: "/addresses/from-location/",
    byId: (id) => `/addresses/${id}/`,
  },

  TAXONOMY: {
    productCategories: "/product-categories/",
    containerOptions: "/container-options/",
    containerTypes: "/container-types/",
    containerMaterials: "/container-materials/",
    containerVolumes: "/container-volumes/",
    fuelTypes: "/fuel-types/",
    brands: "/brands/",
  },

  DELIVERY: {
    types: "/delivery-types/",
    typeById: (id) => `/delivery-types/${id}/`,
    typesForCart: (cartId) => `/carts/${cartId}/delivery-types/`,
  },

  CATALOG: {
    products: "/products/",
    productById: (id) => `/products/${id}/`,
    productPackings: (id) => `/products/${id}/packings/`,
    productsSearch: "/products-search/",
    productsSearchBounds: "/products-search/bounds/",
    productSuppliers: (id) => `/products/${id}/suppliers/`,
    cylindersSummary: "/cylinders/summary/",
    favouriteToggle: (id) => `/products/${id}/favourite/`,
    favourites: "/products/favourites/",
  },

  SUPPLIERS: {
    search: "/suppliers/search/",
    byId: (id) => `/suppliers/${id}/`,
    accessories: (id) => `/suppliers/${id}/accessories/`,
    customRefillQuote: (id) => `/suppliers/${id}/custom-refill/quote/`,
  },

  FAVOURITES: {
    root: "/favourites/",
    mine: "/favourites/mine/",
    byTarget: (targetType, targetId) => `/favourites/${targetType}/${targetId}/`,
  },

  CART: {
    root: "/carts/",
  },

  ORDERS: {
    root: "/orders/",
    info: "/orders/info/",
    reserve: "/orders/reserve/",
    master: "/orders/master/",
    masterReserve: "/orders/master/reserve/",
    masterById: (id) => `/orders/master/${id}/`,
    counts: "/orders/counts/",
    byId: (id) => `/orders/${id}/`,
    cancel: (id) => `/orders/${id}/cancel/`,
    reorder: (id) => `/orders/${id}/reorder/`,
  },

  PAYMENTS: {
    methods: "/payment-methods/",
    paystack: "/payments/paystack/",
    paystackByReference: (reference) =>
      `/payments/paystack/${encodeURIComponent(reference)}/`,
    savedAuthorizations: "/payments/paystack/saved-authorizations/",
    wallet: "/payments/wallet/",
    walletByCurrency: (currencyCode) =>
      `/payments/wallet/${encodeURIComponent(currencyCode)}/`,
    walletTopUpInitiate: "/payments/wallet/top-up/initiate/",
    walletTopUpCallback: "/payments/wallet/top-up/callback/",
    fxQuote: "/payments/fx/quote/",
    cod: "/payments/cod/",
  },

  TRANSACTIONS: {
    root: "/api/transactions/",
    mine: "/payments/transactions/mine/",
  },

  FAQS: {
    root: "/faqs/",
    byId: (id) => `/faqs/${id}/`,
  },

  RATINGS: {
    root: "/ratings/",
    byId: (id) => `/ratings/${id}/`,
    mine: "/ratings/mine/",
    listing: "/ratings/listing/",
    summary: "/ratings/summary/",
    average: "/ratings/average/",
  },

  PAYOUTS: {
    root: "/consumer-payouts/",
    byId: (id) => `/consumer-payouts/${id}/`,
    evaluateFee: "/consumer-payouts/evaluate-fee/",
    request: "/consumer-payouts/request/",
  },

  TRANSFER_RECIPIENTS: {
    root: "/transfer-recipients/",
    byId: (id) => `/transfer-recipients/${id}/`,
    setDefault: (id) => `/transfer-recipients/${id}/default/`,
  },

  BANKING: {
    banks: "/banks/",
    mobileMoneyOperators: "/mobile-money-operators/",
  },

  /**
   * Read-only on web. `subscribe/` and `unsubscribe/` register an FCM device
   * token and are mobile-only — the web client does not call them, so the
   * unread state comes from polling `feed` rather than from a push.
   */
  NOTIFICATIONS: {
    feed: "/notifications/push/",
    unreadCount: "/notifications/push/unread-count/",
    markRead: (id) => `/notifications/push/${id}/read/`,
  },

  EXPLORE: {
    categories: "/explore-categories/",
  },

  CMS: {
    entries: "/api/cms/entries/",
  },

  /**
   * Web-only. Not present in the app's apiConfig because the app never used
   * it, but it is live on the backend ({ currencies: [...] }) and the web
   * currency picker depends on it.
   */
  CURRENCIES: {
    root: "/currencies/",
  },

  /**
   * Web-only bootstrap. `/parameters/` drives getLabel/getConfig and
   * `/operations/` drives hasOperation; both are live. `/apps/init` is NOT
   * here on purpose — it 404s, and its job moved to lib/api/env.js.
   */
  PLATFORM: {
    parameters: "/parameters/",
    operations: "/operations/",
  },
};

export default ENDPOINTS;
